/**
 * Dashboard Theme Configuration
 *
 * Single source of truth for dashboard visual settings.
 * Centralizes colors, chart config, and breakpoints for easy customization.
 *
 * Chart color scheme: Brand Blue (#318CB8) + Silver (#C6D2D5)
 * — sourced from the Hourglass Brand Guidelines primary palette.
 * Blue = primary metric (After / Exported / Hourglass) — the hero color
 * Silver = secondary metric (Before / Unexported / Other) — neutral baseline
 */

// ============================================
// CHART COLORS (Utilization & Export Activity)
// ============================================
// Unified palette: Brand Blue (primary) + Sky Blue (secondary)

export const CHART_COLORS = {
  /**
   * Primary metric (Hourglass / Exported hours):
   * Brand Blue — the strongest accent in the brand palette
   */
  exported: {
    fill: "#318CB8",      // Brand Blue
    stroke: "#2671A0",    // Darkened Blue (legend/stroke)
  },
  /**
   * Secondary metric (Other / Unexported hours):
   * Silver — neutral baseline that recedes behind the blue hero color
   */
  unexported: {
    fill: "#C6D2D5",      // Silver (brand palette)
    stroke: "#8EA6AC",    // Cadet Grey (brand palette — legend/stroke)
  },
  /** Grid and axis styling */
  grid: {
    line: "#d1d5db",      // Gray-300
    axis: "#6b7280",      // Gray-500
  },
};

// ============================================
// CHART CONFIGURATION
// ============================================

export const CHART_CONFIG = {
  /** Maximum bar width in pixels (prevents comically wide bars with few data points) */
  maxBarSize: 80,
  /** Border radius for top of stacked bars [topLeft, topRight, bottomLeft, bottomRight] */
  topBarRadius: [4, 4, 0, 0] as [number, number, number, number],
  /** No radius for bottom bar in stack */
  bottomBarRadius: [0, 0, 0, 0] as [number, number, number, number],
};

// ============================================
// TEXT COLORS (for tooltips, tables, cards)
// ============================================

export const TEXT_COLORS = {
  exported: {
    light: "text-[#2671A0] dark:text-[#7DBFD5]",
    value: "text-[#2671A0] dark:text-[#7DBFD5]",
  },
  unexported: {
    light: "text-[#8EA6AC] dark:text-[#C6D2D5]",
    value: "text-[#8EA6AC] dark:text-[#C6D2D5]",
  },
};

// ============================================
// SUMMARY CARD STYLES
// ============================================

export const SUMMARY_CARD_STYLES = {
	/** Base card style - compact, clean */
	base: 'p-3 rounded-lg bg-card border border-border shadow-xs',
	/** Accent bar colors (the left vertical stripe) */
	accent: {
		exported: 'bg-[#318CB8] dark:bg-[#7DBFD5]',
		unexported: 'bg-[#C6D2D5] dark:bg-[#8EA6AC]',
		neutral: 'bg-slate-400 dark:bg-slate-500',
		count: 'bg-slate-400 dark:bg-slate-500',
	},
}

// ============================================
// KPI CARD STYLES (shared across dashboard tabs)
// ============================================

export const KPI_CARD_STYLES = {
  /** Dark green gradient — primary metrics (old dashboard: Exported badge) */
  green: "from-[var(--brand-dark-teal)] to-[var(--brand-dark-green)]",
  /** Teal gradient — secondary metrics (old dashboard: Unexported badge) */
  teal: "from-[#2D5A6B] to-[var(--brand-dark-teal)]",
  /** Blue gradient — tertiary metrics (old dashboard: Deleted badge) */
  blue: "from-[#318CB8] to-[var(--brand-dark-teal)]",
  /** Dark gradient — neutral/count metrics (old dashboard: Active Users badge) */
  dark: "from-[var(--brand-dark-green)] to-[#0f1a1b]",
  /** Slate gradient (kept for backward compat if needed) */
  slate: "from-[var(--brand-dark-slate)] to-[var(--brand-mid-slate)]",
} as const;

// ============================================
// YEAR-OVER-YEAR CHART COLORS
// ============================================

export const YOY_CHART_COLORS = {
  before: "#C6D2D5", // Silver — prior year baseline (recedes)
  after: "#318CB8",  // Brand Blue — Hourglass period (hero)
} as const;

/** Layout config for the YoY faceted chart grid (all-professionals view) */
export const YOY_CHART_LAYOUT = {
  /** Height (px) for the single full-width chart when one professional is selected */
  singleChartHeight: 350,
  /** Height (px) for each mini-chart in the faceted grid */
  gridChartHeight: 300,
  /** Tailwind grid classes — controls columns and spacing for the faceted layout */
  gridClass: "grid grid-cols-1 lg:grid-cols-2 gap-6",
} as const;

// ============================================
// DAILY VIEW CHART COLORS
// ============================================

export const DAILY_CHART_COLORS = {
  after: "#318CB8",    // Brand Blue — "After" (Hourglass period) daily line
  before: "#C6D2D5",   // Silver — "Before" (prior year) daily line
  afterMA: "#f59e0b",  // Amber — after 7-day moving average dots (contrast against blue)
  beforeMA: "#6366f1", // Indigo — before 7-day moving average dots
} as const;

// ============================================
// UTILIZATION STYLES
// ============================================

export const UTILIZATION_COLORS = {
  hourglass: "#318CB8",  // Brand Blue (matches CHART_COLORS.exported.fill)
  other: "#C6D2D5",      // Silver (matches CHART_COLORS.unexported.fill)
} as const;

// ============================================
// PERFORMANCE TABLE STYLES
// ============================================

export const PERF_TABLE_COLORS = {
  utilizationHeader: "#7c6f9e", // muted violet
  weekHeader: "#2a7f90", // teal
  monthHeader: "#2e8b70", // sage green
  yearHeader: "#3b78a8", // steel blue
  positive: "#16a34a",
  negative: "#ef4444",
} as const;

// ============================================
// LAYOUT BREAKPOINTS
// ============================================

export const LAYOUT = {
  /** Breakpoint for side-by-side chart layout (Tailwind xl = 1280px) */
  sideBySideBreakpoint: "xl",
  /** Chart container height */
  chartHeight: "h-80",
};
